import { Loader } from '@domains/billing-settings/priv/CardGroup/components/Loader'
import ErrorIcon from '@mui/icons-material/Error'
import { Skeleton as _Skeleton, Button, FormControl, InputLabel, MenuItem, Select, Stack, Tooltip, Typography } from '@mui/material'
import { Controller, useForm } from 'react-hook-form'
import { type CardGroup, cardGroupOptions } from '../api/api'
import { BorderedStack } from '../components/BorderedStack'

export interface SelectCardGroupProps {
  onSelectCardGroup: (group: CardGroup) => void
  onRemoveOverride: () => void
  group: CardGroup | undefined
  isOverridden: boolean
  isLoading: boolean
  isPatching: boolean
  isDeleting: boolean
  isError: boolean
}

interface CardGroupFormValues {
  group: CardGroup | undefined
}

export const SelectCardGroup = ({
  onSelectCardGroup,
  onRemoveOverride,
  group,
  isOverridden,
  isLoading,
  isPatching,
  isDeleting,
  isError,
}: SelectCardGroupProps) => {
  const form = useForm<CardGroupFormValues>({
    values: { group },
  })

  const handleSelect = ({ group }: CardGroupFormValues) => {
    if (!group) return

    onSelectCardGroup(group)
  }

  return (
    <BorderedStack
      sx={{
        justifyContent: 'space-between',
        flex: 1,
        padding: 2,
      }}
    >
      <Stack
        sx={{
          gap: 3,
        }}
      >
        <Stack>
          <Stack
            direction="row"
            sx={{
              gap: 1,
              alignItems: 'center',
            }}
          >
            <Typography variant="p2Medium">FX surcharge</Typography>
            {isOverridden && (
              <Tooltip placement="top" title="This value has been changed from the default on the subscription plan">
                <ErrorIcon data-testid="override-warning" fontSize="inherit" color="warning" />
              </Tooltip>
            )}
          </Stack>
          {isError && (
            <Typography data-testid="error-message" variant="p4Regular" color="error">
              An error occurred while loading the card group
            </Typography>
          )}
        </Stack>
        <Loader isLoading={isLoading} loader={SelectCardGroup.SelectSkeleton}>
          <Controller
            control={form.control}
            name="group"
            render={({ field }) => (
              <FormControl fullWidth>
                <InputLabel>Card group</InputLabel>
                <Select
                  disabled={isPatching || isDeleting || isError}
                  inputProps={{ 'data-testid': 'card-group-select-input' }}
                  label="Card group"
                  value={field.value ?? ''}
                  onChange={(event) => field.onChange(event.target.value as CardGroup)}
                >
                  {cardGroupOptions.map(({ value, label, disabled }) => (
                    <MenuItem key={value} value={value} disabled={disabled}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          />
        </Loader>
      </Stack>
      <Stack
        direction="row"
        sx={{
          justifyContent: 'space-between',
        }}
      >
        <Button
          data-testid="save-override-button"
          size="medium"
          variant="contained"
          loading={isPatching}
          disabled={isLoading || !form.formState.isDirty || isError}
          onClick={form.handleSubmit(handleSelect)}
        >
          Save
        </Button>
        {isOverridden && (
          <Button
            variant="text"
            size="medium"
            color="error"
            loading={isDeleting}
            disabled={isLoading}
            data-testid="remove-override-button"
            onClick={onRemoveOverride}
          >
            Delete override
          </Button>
        )}
      </Stack>
    </BorderedStack>
  )
}

const Skeleton = (props) => <_Skeleton {...props} sx={{ transform: 'none' }} />

SelectCardGroup.SelectSkeleton = () => <Skeleton data-testid="loading-skeleton" height={48} />
